import { KirjanpitajanRooli } from '@lemon/core-firebase/lemonator/model/kirjanpitaja'
import { BaseEntity, ErrorResponse, LocalDate, NumberDate, Timestamp } from '@lemon/core/model/common'
import { MonikielinenTeksti } from './kirjanpito'
import { LemonatorNakyma } from './lemonator-nakyma'

export const EI_KIRJANPITAJAA_AVAIN = 'eitiedossa'
export interface ImpersonoidunKayttajanTiedot {
  avain: string
  etunimi: string
  sukunimi: string
  email: string
}

export interface ImpersonoidunAsiakkaantTiedot {
  avain: string
  nimi: string
  ytunnus: string
  asiakasId: number
}

export type KirjanpitoRaporttiValilehti = 'tase-ja-tulos' | 'viralliset' | 'paakirja' | 'bruttolaskelma' | 'oss-myynnit' | 'alv-laskelma'
export type KirjanpitoRaporttiEnsinNaytettava = 'tulos' | 'tase'
export interface KirjanpitajanAsetukset {
  kirjanpitajanAvain: string
  /**
   * Number must be in format yymm
   */
  asiakasListausAlkuKk: number
  /**
   * Number must be in format yymm
   */
  asiakasListausLoppuKk: number
  /**
   * Kirjanpidossa oletuksena avoinna oleva välilehti
   */
  kirjanpidonOletusraportti: KirjanpitoRaporttiValilehti
  /**
   * Näkymissä, joissa on samaan aikaan sekä tulos, että tase, kumpi näytetään ensin?
   */
  kirjanpidonEnsinNaytettevaRaportti: KirjanpitoRaporttiEnsinNaytettava
}

export interface Kirjanpitaja extends BaseEntity {
  etunimi: string
  sukunimi: string
  email: string
  puhelin?: string
  kuvaUrl?: string
  signatureImageUrl?: string
  lemonaidUid: string
  lemonaidAsiakasAvain: string
  impersonoituKayttaja?: ImpersonoidunKayttajanTiedot
  impersonoituAsiakas?: ImpersonoidunAsiakkaantTiedot
  /**
   * Number must be in format yymmdd
   */
  aloitti: number
  toimipisteAvain?: string
  /** @deprecated */
  workTimeSettings?: KirjanpitajaWorkTimeChange[]

  emailEndText?: MonikielinenTeksti
  lomaviesti?: MonikielinenTeksti
  tutkinnot?: KirjanpitajanTutkinnot
  allowedViews?: { [viewKey in LemonatorNakyma]?: true }
}

export interface KirjanpitajanTutkinnot {
  klt?: boolean
}

/** @deprecated */
export interface KirjanpitajaWorkTimeChange {
  percentage: number
  /** Date must be the first day of month! */
  validFrom: NumberDate
  changedAt: Timestamp
  changedBy: string
}

export interface KirjanpitajanNimitiedot extends Pick<
  Kirjanpitaja,
  'avain' | 'etunimi' | 'sukunimi' | 'email' | 'puhelin' | 'toimipisteAvain' | 'signatureImageUrl' | 'emailEndText' | 'lomaviesti' | 'lemonaidUid' | 'poistettu' | 'tutkinnot'
> {}

export interface KirjanpitajanSarake {
  aktiivinen: boolean
  avain: string
  nimi: string
  tilat: KirjanpitajanSarakkeenTila[]
  tallentanut: string
  tallennettu: Timestamp
  jarjestys: number
}

export interface KirjanpitajanSarakkeenTila {
  avain: string
  nimi: string
  kuvaus: string
  vari: string
  oletus: boolean
}

export interface KirjanpitajanSarakkeenArvo {
  /**
   * Arvo (Value)
   */
  v: string
  /**
   * Päivitetty, muotoa yymmddhhmmss (Updated)
   */
  u: number
  /**
   * Päivittäjä
   */
  p: string
}

/**
 * kirjanpitajat/{kirjanpitajanAvain}/asiakaslistauksen-sarakkeiden-arvot/{asiakasAvain}
 */
export interface KirjanpitajanSarakkeidenArvotAsiakkaalle {
  /**
   * Asiakkaan avain
   */
  a: string
  /**
   * Sarakkeiden arvot (Values)
   */
  v: { [sarakkeenAvain: string]: KirjanpitajanSarakkeenArvo }
  /**
   * Primääri kirjanpitäjä
   */
  p: string
  /**
   * Sekundäärinen (vara)kirjanpitäjä
   */
  s?: string
}

export interface KirjanpitajanAsiakkaat extends BaseEntity {
  kirjanpitajanAsiakkaat: KirjanpitajanAsiakas[]
  kirjanpitajanAsiakkaatVara: KirjanpitajanAsiakas[]
}

export interface KirjanpitajanAsiakas {
  asiakasAvain: string
  voimassaoloAlkaa: LocalDate
  voimassaoloPaattyy: LocalDate | null
}

export interface AloitaKayttajanaKirjautuminenPyynto {
  asiakasAvain: string
  kayttajaAvain: string
}

export interface AloitaKayttajanaKirjautuminenVastaus {}

export interface LopetaKayttajanaKirjautuminenPyynto {}

export interface LopetaKayttajanaKirjautuminenVastaus {}

export interface AnnaKirjanpitajienNimitiedotPyynto {}

export interface AnnaKirjanpitajienNimitiedotVastaus {
  nimitiedot: KirjanpitajanNimitiedot[]
}

export interface KatkaiseKirjanpitajanSessioPyynto {
  kirjanpitajanAvain: string
}
export interface KatkaiseKirjanpitajanSessioVastaus extends ErrorResponse {}

export interface LisaaUusiKirjanpitajaPyynto {
  etunimi: string
  sukunimi: string
  email: string
  puhelin: string | null
  aloitti: NumberDate
  toimipisteAvain: string
  tutkintoKlt: boolean
  msUid: string
  /** Alkurooli (vain IT/KAYTTOTUKI saa asettaa). null = käyttäjällä ei ole roolia (ei pääse kirjautumaan Lemonatoriin). */
  rooli: KirjanpitajanRooli | null
}

export interface LisaaUusiKirjanpitajaVastaus extends ErrorResponse<'unauthorized' | 'missing-data' | 'email-already-exists' | 'unknown-error'> {
  kirjanpitajaAvain?: string
}

export interface KirjanpitajanKuvanUploadRequest {
  kirjanpitajanAvain: string
  fileName: string
  fileBase64: string
}
export interface KirjanpitajanKuvanUploadResponse extends ErrorResponse {}

export interface KirjanpitajanToimipiste {
  avain: string
  nimi: string
  katuosoite: string
  postinro: string
  kaupunki: string
  maa: string
  puhnro: string
}

export interface KirjanpitajanTiiminJasen {
  kirjanpitajaAvain: string
  /** Milloin aloitti tiimissä */
  alku: NumberDate
  /** Milloin lopetti tiimissä */
  loppu: NumberDate | null
  /** Onko henkilön primary tiimi */
  primary?: boolean
}

export interface KirjanpitajanTiimi {
  avain: string
  nimi: string
  esihenkilot: KirjanpitajanTiiminJasen[]
  /** Milloin perustettiin */
  alku: NumberDate
  /** Milloin lopetettiin */
  loppu: NumberDate | null
  jasenet: KirjanpitajanTiiminJasen[]
}

export interface KirjanpitajanTiimiTiimikokonaisuudessa {
  avain: KirjanpitajanTiimi['avain']
  /** Milloin aloitti kokonaisuudessa */
  alku: NumberDate
  /** Milloin lopetti kokonaisuudessa */
  loppu: NumberDate | null
}

/**
 * Tiimikokonaisuus tarkoittaa kokoelmaa tiimejä, joilla on yksi tai useampi esihenkilö.
 */
export interface KirjanpitajanTiimikokonaisuus {
  avain: string
  nimi: string
  /** Milloin perustettiin */
  alku: NumberDate
  /** Milloin lopetettiin */
  loppu: NumberDate | null
  esihenkilot: KirjanpitajanTiiminJasen[]
  tiimit: KirjanpitajanTiimiTiimikokonaisuudessa[]
}

/**
 * Firestore-tallennusmuoto: esihenkilot/jasenet mappina kirjanpitäjän avaimella (sama henkilö voi
 * olla useammalla erillisellä jaksolla, siksi arvo on array). Mahdollistaa yksittäisen jäsenyyden
 * päivittämisen ilman koko taulukon read-modify-write -sykliä.
 * Service muuntaa tämän muodon julkiseksi KirjanpitajanTiimi-shape:ksi load-vaiheessa.
 */
export interface KirjanpitajanTiimiVarastossa extends Omit<KirjanpitajanTiimi, 'esihenkilot' | 'jasenet'> {
  esihenkilot: { [kirjanpitajaAvain: string]: KirjanpitajanTiiminJasen[] }
  jasenet: { [kirjanpitajaAvain: string]: KirjanpitajanTiiminJasen[] }
}

export interface KirjanpitajanTiimikokonaisuusVarastossa extends Omit<KirjanpitajanTiimikokonaisuus, 'esihenkilot'> {
  esihenkilot: { [kirjanpitajaAvain: string]: KirjanpitajanTiiminJasen[] }
}

// admin/kirjanpitajien-tiimit
export interface KirjanpitajienTiimit {
  tiimit: Partial<Record<KirjanpitajanTiimi['avain'], KirjanpitajanTiimiVarastossa>>
  tiimikokonaisuudet: Partial<Record<KirjanpitajanTiimikokonaisuus['avain'], KirjanpitajanTiimikokonaisuusVarastossa>>
}

/**
 * Vakiot tunnetuille tiimiavaimille. Käytä näitä koodissa kun tarkistelet tiettyä tiimiä
 * (esim. IT/KAYTTOTUKI-suojaukset, GENOVA-hallintotarkistukset). Uudet tiimit voidaan luoda
 * UI:sta ilman koodimuutosta — vakio tarvitsee lisätä tänne vain jos koodissa pitää erikseen
 * viitata tiimiin nimen perusteella.
 */
export class KirjanpitajaTiimiAvaimet {
  // static readonly SUKKELA = 'SUKKELA' // Sukkela
  static readonly LUNARIO = 'LUNARIO' // APL Balance
  static readonly AMALFI = 'TULOSNURKKA' // Tiimi nimettiin uudelleen Amalfiksi, mutta avain pysyy historiasyistä 'TULOSNURKKA' (dataa Firestoressa ei migratoitu)
  static readonly MONACHELLO = 'MONACHELLO'
  static readonly VERNA = 'VERNA'
  static readonly FINO = 'FINO'
  static readonly EUREKA = 'EUREKA'
  static readonly GENOVA = 'GENOVA'
  static readonly DIAMANTE = 'DIAMANTE'
  static readonly VARIEGATA = 'VARIEGATA'
  static readonly LIMETTA = 'LIMETTA'
  static readonly IT = 'IT'
  static readonly KAYTTOTUKI = 'KAYTTOTUKI'
  static readonly SALES = 'SALES'
  static readonly PALKKA = 'PALKKA'
  static readonly MUUT = 'MUUT'
  static readonly EI_TIIMIA = 'ei_tiimia'
}

/**
 * Vakiot tunnetuille tiimikokonaisuus-avaimille. Sama periaate kuin {@link KirjanpitajaTiimiAvaimet}.
 */
export class KirjanpitajaTiimikokonaisuusAvaimet {
  static readonly SILTASAARENKATU = 'SILTASAARENKATU'
}
